/**
 * Create/edit product modal. The parent owns the form state (it is shared
 * with the AI scanner prefill flow); this component only renders the fields.
 */
import { Sparkle } from "@phosphor-icons/react";
import { CATEGORIES, CONDICIONES, Field } from "@/components/inventory/shared";

export function ProductFormModal({
  open,
  setOpen,
  form,
  setForm,
  onSave,
  editingId,
  suggesting,
  suggestEspecificaciones,
}) {
  if (!open) return null;
  return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form
            className="bg-white border border-zinc-200 max-w-2xl w-full p-8 animate-fade-in"
            onClick={(e) => e.stopPropagation()}
            onSubmit={onSave}
            data-testid="product-form"
          >
            <div className="overline text-klein">{editingId ? "Editar" : "Nuevo"}</div>
            <h2 className="h-display text-3xl font-bold tracking-tight mt-1 mb-6">
              {editingId ? "Editar producto" : "Producto nuevo"}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Nombre" full>
                <input
                  className="input-flat"
                  required
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  data-testid="input-nombre"
                  data-help="Nombre comercial del producto. Se usa en publicaciones, ventas y reportes."
                />
              </Field>
              <Field label="Categoría">
                <select
                  className="input-flat"
                  value={form.categoria}
                  onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                  data-testid="select-categoria"
                  data-help="Categoría a la que pertenece. Agrupa productos en el dashboard y ayuda a generar copy más preciso."
                >
                  {CATEGORIES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </Field>
              <Field label="Condición">
                <select
                  className="input-flat"
                  value={form.condicion}
                  onChange={(e) => setForm({ ...form, condicion: e.target.value })}
                  data-testid="select-condicion"
                  data-help="Estado del artículo: nuevo, usado, restaurado u 'oportunidad'. Determina el rango de precios sugerido."
                >
                  {CONDICIONES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </Field>
              <Field label="Costo">
                <input
                  type="number"
                  step="0.01"
                  className="input-flat"
                  value={form.costo}
                  onChange={(e) => setForm({ ...form, costo: e.target.value })}
                  data-testid="input-costo"
                  data-help="Cuánto te costó adquirirlo (incluye envío/comisiones). Se usa para calcular utilidad y margen."
                />
              </Field>
              <Field label="Precio de venta">
                <input
                  type="number"
                  step="0.01"
                  className="input-flat"
                  value={form.precio_venta}
                  onChange={(e) => setForm({ ...form, precio_venta: e.target.value })}
                  data-testid="input-venta"
                  data-help="Precio al que lo ofreces al público. Se muestra en la página pública del producto."
                />
              </Field>
              <Field label="Cantidad">
                <input
                  type="number"
                  className="input-flat"
                  value={form.cantidad}
                  onChange={(e) => setForm({ ...form, cantidad: e.target.value })}
                  data-testid="input-cantidad"
                  data-help="Stock disponible. Cuando llegue a 0 el producto se marca agotado en la página pública."
                />
              </Field>
              <Field label="Descripción" full>
                <textarea
                  className="input-flat min-h-[80px]"
                  value={form.descripcion}
                  onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                  data-testid="input-descripcion"
                  data-help="Descripción que verán los compradores. La IA también la usa para generar copy de redes sociales."
                />
              </Field>
              <Field label="Especificaciones técnicas (para el chat IA)" full>
                <div className="relative">
                  <textarea
                    className="input-flat min-h-[100px] pr-28"
                    value={form.especificaciones}
                    onChange={(e) => setForm({ ...form, especificaciones: e.target.value })}
                    placeholder="• Marca / modelo&#10;• Medidas&#10;• Material&#10;• Garantía&#10;• Qué incluye&#10;Si se llena con puntos, la IA del chat usa SOLO estos datos y no inventa."
                    data-testid="input-especificaciones"
                    data-help="Datos técnicos concretos del producto. Cuanto más específicos, mejor responde la IA del chat público. Si lo dejas vacío, puedes pulsar 'Sugerir con IA' para que la IA arme una base que puedes editar."
                  />
                  <button
                    type="button"
                    onClick={suggestEspecificaciones}
                    disabled={suggesting || !form.nombre}
                    className="absolute right-2 top-2 inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold bg-klein text-white hover:bg-klein-dark disabled:opacity-50"
                    title={!form.nombre ? "Primero escribe el nombre del producto" : "La IA genera una base que puedes editar"}
                    data-testid="btn-suggest-especificaciones"
                  >
                    <Sparkle size={11} weight="fill" />
                    {suggesting ? "Pensando…" : "Sugerir con IA"}
                  </button>
                </div>
              </Field>
              <div className="col-span-1 sm:col-span-2 mt-2 p-3 border border-zinc-200 bg-zinc-50">
                <label className="flex items-start gap-3 cursor-pointer" data-help="Si lo activas, la app genera SOLA los borradores de Instagram, Facebook y WhatsApp en cuanto guardes. Aparecerán en 'Bandeja de publicación' listos para enviarlos con 1 click.">
                  <input
                    type="checkbox"
                    checked={!!form.auto_publish}
                    onChange={(e) => setForm({ ...form, auto_publish: e.target.checked })}
                    className="mt-0.5 accent-klein w-4 h-4"
                    data-testid="input-auto-publish"
                  />
                  <div className="text-sm">
                    <div className="font-semibold flex items-center gap-2">
                      🚀 Publicar en automático
                    </div>
                    <div className="text-xs text-muted2 mt-0.5">
                      La IA genera SOLA borradores listos para Instagram, Facebook y WhatsApp en cuanto guardes este producto. Los verás en <strong>Bandeja de publicación</strong> con botones para enviarlos en 1 click. Telegram te avisa cuando estén listos.
                    </div>
                  </div>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost" data-testid="btn-cancel" data-help="Cierra el formulario sin guardar.">
                Cancelar
              </button>
              <button type="submit" className="btn-klein" data-testid="btn-save-product" data-help="Guarda los cambios en el inventario.">
                Guardar
              </button>
            </div>
          </form>
        </div>
  );
}
